import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  TablePagination,
} from "@mui/material";
import AddHomeWorkOutlinedIcon from "@mui/icons-material/AddHomeWorkOutlined";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { ResponsiveTable } from "@/components/Responsive";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import type { RegistryChangeAlertEntity } from "@/features/registryChangeAlert/types";
import { DismissButton } from "@/features/registryChangeAlert/components/DismissButton";
import { NewChip } from "@/features/registryChangeAlert/components/NewChip";
import { SectionChip } from "@/features/registryChangeAlert/components/SectionChip";
import { formatReceivedDate } from "@/features/registryChangeAlert/utils/makeColumns";
import { ROWS_PER_PAGE_OPTIONS } from "@/features/registryChangeAlert/utils/constants";

/** SP のカードに出す項目（全項目をカードに出し、「詳細」ダイアログは使わない）。 */
const HEADERS = [
  "選択",
  "種類",
  "所在",
  "地番・家屋番号",
  "取得済み",
  "新しい登記",
  "変動",
  "操作",
];

interface IProps {
  alerts: RegistryChangeAlertEntity[];
  rowCount: number;
  loading: boolean;
  page: number;
  pageSize: number;
  selectionModel: GridSelectionModel;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSelectionModelChange: (model: GridSelectionModel) => void;
  onAcquire: (alert: RegistryChangeAlertEntity) => void;
  onDismiss: (alert: RegistryChangeAlertEntity) => void;
  /** 読み取り専用ユーザーは「非表示」を押せない。 */
  isReadOnly: boolean;
}

/** 変動アラート一覧の SP 表示。表形式ではなく ResponsiveTable（1 行 = 1 カード）で出す。選択・ページングは PC の一覧と同じ状態を使う。 */
export const RegistryChangeAlertResponsiveList: React.FC<IProps> = ({
  alerts,
  rowCount,
  loading,
  page,
  pageSize,
  selectionModel,
  onPageChange,
  onPageSizeChange,
  onSelectionModelChange,
  onAcquire,
  onDismiss,
  isReadOnly,
}) => {
  const selected = new Set(selectionModel.map(String));

  const toggle = (id: number): void => {
    const key = String(id);
    onSelectionModelChange(
      selected.has(key)
        ? selectionModel.filter((value) => String(value) !== key)
        : [...selectionModel, id]
    );
  };

  const rows = alerts.map((alert) => [
    <Checkbox
      key="select"
      checked={selected.has(String(alert.id))}
      onChange={() => {
        toggle(alert.id);
      }}
      inputProps={{ "aria-label": "行を選択" }}
      sx={{ p: 1.25 }}
    />,
    <Stack
      key="type"
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.5}
    >
      {!alert.isRead && <NewChip />}
      <span>{`${alert.pictureTypeLabel}／${alert.realEstateTypeLabel}`}</span>
    </Stack>,
    alert.location,
    alert.chibanKaokuNumber,
    formatReceivedDate(alert.baseReceivedDate),
    formatReceivedDate(alert.newerReceivedDate),
    <Stack
      key="sections"
      direction="row"
      flexWrap="wrap"
      justifyContent="flex-end"
      gap={0.5}
    >
      {alert.changedSections.map((section) => (
        <SectionChip key={section.value} label={section.label} />
      ))}
    </Stack>,
    <Stack
      key="actions"
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="wrap"
      useFlexGap
      gap={0.5}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddHomeWorkOutlinedIcon />}
        onClick={() => {
          onAcquire(alert);
        }}
        sx={{
          whiteSpace: "nowrap",
          textTransform: "none",
          width: "auto",
          minHeight: MIN_TAP_TARGET,
        }}
      >
        リユース取得
      </Button>
      <DismissButton
        isReadOnly={isReadOnly}
        sx={{ width: MIN_TAP_TARGET, height: MIN_TAP_TARGET }}
        onClick={() => {
          onDismiss(alert);
        }}
      />
    </Stack>,
  ]);

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <ResponsiveTable
          title="取得済み登記の変動アラート"
          headers={HEADERS}
          rows={rows}
          emptyMessage="変動のあった物件はありません。"
        />
      )}
      <TablePagination
        component="div"
        count={rowCount}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage="件数"
        labelDisplayedRows={({ from, to, count }) =>
          `${count}件中 ${from}〜${to}件`
        }
        onPageChange={(_, nextPage) => {
          onPageChange(nextPage);
        }}
        onRowsPerPageChange={(event) => {
          onPageSizeChange(Number(event.target.value));
        }}
      />
    </Box>
  );
};
